import * as React from "react";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { toast } from "sonner";
import { GitMerge, Loader2, AlertTriangle, FolderTree, ListFilter, CheckCheck, ListChecks } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import { ConflictLegend } from "./ConflictLegend";
import { ActionSelectItem } from "./ActionSelectItem";
import { ConflictGroupCard, isGroupResolved } from "./ConflictGroupCard";
import { conflictRulePagePath } from "@/models/ConflictRule";
import { QUERY_CONFIG } from "../../../../constants/QueryConfig";
import {
  ConflictGroupDto,
  ConflictGroupFileDto,
  ConflictResolutionItemDto,
} from "../../../../models/ConflictGroup";
import {
  ConflictReason,
  ConflictReasonColors,
  ConflictReasonIcons,
  ConflictReasonLabels,
} from "../../../../models/ConflictReason";
import {
  buildResolveBatches,
  extensionOf,
  groupResolveState,
  isDisplacedWinner,
  isHeldForAWinnerThatWillNotLand,
  isTerminalFile,
  isWinner,
  resolvedActionFor,
} from "../../../../lib/conflictResolution";
import {
  ConflictResolutionAction,
  ConflictResolutionActionColors,
  ConflictResolutionActionDescriptions,
  ConflictResolutionActionLabels,
  ConflictResolutionOutcomeShortLabels,
  defaultResolutionAction,
  isDestructiveResolution,
} from "../../../../models/ConflictResolutionAction";
import { MigrationFlagStatus } from "../../../../models/Flags/MigrationFlagStatus";
import { ApiErrorWithMessages } from "../../../../lib/apiError";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

/** A conflict still holding its file back. Resolved and acknowledged ones are shown only to change a decision. */
const isOpenConflict = (file: ConflictGroupFileDto) =>
  file.conflictStatus === MigrationFlagStatus.UnResolved;

/** A refused resolve, one line per group the server refused (each names the path and why). */
interface ResolveFailure {
  title: string;
  messages: string[];
}

const failureMessages = (error: unknown, fallback: string): string[] =>
  error instanceof ApiErrorWithMessages
    ? error.messages
    : [error instanceof Error && error.message ? error.message : fallback];

interface ConflictResolveViewProps {
  /** Parent entity id (caseId / intakeId / providerId / templatesLibraryId / firmDocumentsFolderId). */
  parentId: string;
  /** Singular, lowercase noun for the entity ("case", "intake", …) used in copy. */
  entityNoun: string;
  /** react-query key for the conflict-groups query (per entity). */
  groupsQueryKey: readonly unknown[];
  /** File-list query key(s) to invalidate after a successful resolve. */
  invalidateQueryKeys: readonly unknown[][];
  /** Fetches the conflict groups for `parentId`; `includeResolved` also returns resolved/acknowledged ones. */
  fetchGroups: (parentId: string, includeResolved: boolean) => Promise<ConflictGroupDto[]>;
  /** Submits a set of resolutions as one ResolveConflictGroup POST. */
  resolveGroup: (resolutions: ConflictResolutionItemDto[]) => Promise<void>;
  disabled?: boolean;
}

/** Actions offered by the bulk "set all" control — never the destructive ReplaceFromDisk. */
const BULK_ACTIONS: ConflictResolutionAction[] = [
  ConflictResolutionAction.AcceptSharePoint,
  ConflictResolutionAction.RenameKeepBoth,
  ConflictResolutionAction.SkipAcknowledge,
];

/**
 * Drawer for resolving SharePoint conflict groups. Per group the operator may nominate one winner
 * (ReplaceFromDisk); the rest default to a non-destructive action and can be overridden, then the group is sent
 * as a single ResolveConflictGroup POST. For large backlogs (e.g. Office files SharePoint re-packages into
 * perpetual size/hash mismatches), the toolbar adds reason/extension filters, a "set all" action, and a
 * "Resolve all matching" that resolves every eligible visible group in batches — routed through the same
 * per-file resolve path so the files un-block, re-queue, and the client adopts them.
 * The "Show resolved" toggle also lists resolved/acknowledged conflicts so an earlier decision can be changed.
 */
export const ConflictResolveView: React.FC<ConflictResolveViewProps> = ({
  parentId,
  entityNoun,
  groupsQueryKey,
  invalidateQueryKeys,
  fetchGroups,
  resolveGroup,
  disabled,
}) => {
  const queryClient = useQueryClient();
  const { organizationId } = useParams<{ organizationId?: string }>();
  const [open, setOpen] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  // fileId -> chosen resolution action. Seeded lazily as groups load.
  const [actions, setActions] = useState<Record<string, ConflictResolutionAction>>({});
  // Files whose action the operator picked in this session. A settled or already-decided conflict is sent again
  // only when it is in here: re-sending the pre-selected action would silently record a decision nobody made — for a
  // file settled by upload, an Accept SharePoint copy that stops its later edits from migrating.
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [submittingPath, setSubmittingPath] = useState<string | null>(null);
  // Target path of the group awaiting confirmation because its resolution overwrites the SharePoint copy.
  const [confirmingPath, setConfirmingPath] = useState<string | null>(null);

  // Multi-file groups collapse; a fully-resolved group starts collapsed. An explicit toggle overrides the default.
  const [collapseOverride, setCollapseOverride] = useState<Record<string, boolean>>({});
  // Callback ref (via state, NOT useRef) for the virtualizer's scroll element: a plain ref doesn't re-render when
  // the Sheet mounts its scroll container, so the virtualizer would init against a null element and render zero
  // rows until an unrelated state change. Setting state on mount forces the re-render that lets it measure.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  // Bulk-resolution controls: narrow the visible groups, then resolve all eligible ones at once.
  const [reasonFilter, setReasonFilter] = useState<ConflictReason | "all">("all");
  const [extensionFilter, setExtensionFilter] = useState<string | "all">("all");
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [resolveFailure, setResolveFailure] = useState<ResolveFailure | null>(null);

  const groupsQuery = useQuery({
    queryKey: [...groupsQueryKey, showResolved],
    queryFn: async () => await fetchGroups(parentId, showResolved),
    // GetConflictGroups is Editor-only; gating on `disabled` avoids a guaranteed 403 for Viewer-role users.
    enabled: !!parentId && !disabled,
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchOnMount: true,
  });

  const groups = groupsQuery.data ?? [];
  const totalConflicts = groups.reduce((sum, g) => sum + g.files.filter(isOpenConflict).length, 0);

  /** The action a file will resolve with: the operator's choice, else its prior resolution, else the default. */
  const actionFor = (file: ConflictGroupFileDto): ConflictResolutionAction =>
    resolvedActionFor(file, actions);

  /** Winner count + validity for a group under the current per-file selections. */
  const groupState = (group: ConflictGroupDto) => groupResolveState(group, actions);

  // Seed a default action for any file not yet chosen this session; a previously-resolved file starts on
  // the action it was resolved with.
  React.useEffect(() => {
    if (!groups.length) return;
    setActions((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const group of groups) {
        for (const file of group.files) {
          if (next[file.fileId] === undefined) {
            next[file.fileId] =
              file.conflictResolution ?? defaultResolutionAction(file.conflictReason);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [groups]);

  /**
   * Sets a file's action. Enforces a single winner per group: making a file that
   * can still upload the winner (ReplaceFromDisk) demotes the group's other winner
   * to its default action, and the demoted file is sent with the group, because
   * the server refuses a second winner. A Finished or Not Found file is never a
   * winner, so its recorded decision is left as it is.
   */
  const setFileAction = (
    group: ConflictGroupDto,
    file: ConflictGroupFileDto,
    action: ConflictResolutionAction
  ) => {
    const demoted =
      action === ConflictResolutionAction.ReplaceFromDisk && !isTerminalFile(file)
        ? group.files.filter(
            (sibling) => sibling.fileId !== file.fileId && isWinner(sibling, actions)
          )
        : [];
    setTouched((prev) => {
      const next = new Set(prev).add(file.fileId);
      for (const sibling of demoted) next.add(sibling.fileId);
      return next;
    });
    setActions((prev) => {
      const next = { ...prev, [file.fileId]: action };
      for (const sibling of demoted) next[sibling.fileId] = defaultResolutionAction(sibling.conflictReason);
      return next;
    });
  };

  /** Resolve a group, but first require confirmation when the pending actions overwrite the SharePoint copy. */
  const requestResolveGroup = (group: ConflictGroupDto) => {
    const overwrites = filesToSubmit(group).some((file) => isDestructiveResolution(actionFor(file)));
    if (overwrites) setConfirmingPath(group.targetPath);
    else handleResolveGroup(group);
  };

  /**
   * The files a group submit sends: every open conflict, any other whose decision the operator changed, and a copy held
   * for a winner that will not release it, which goes with its current decision so the server releases it.
   */
  const filesToSubmit = (group: ConflictGroupDto) =>
    group.files.filter(
      (file) =>
        isOpenConflict(file) ||
        (touched.has(file.fileId) && actionFor(file) !== file.conflictResolution) ||
        isHeldForAWinnerThatWillNotLand(file)
    );

  const handleResolveGroup = async (group: ConflictGroupDto) => {
    const resolutions: ConflictResolutionItemDto[] = filesToSubmit(group).map((file) => ({
      fileId: file.fileId,
      action: actions[file.fileId] ?? defaultResolutionAction(file.conflictReason),
    }));

    if (resolutions.length === 0) {
      setConfirmingPath(null);
      toast.info("Nothing to change: pick a different action for a file to change its decision");
      return;
    }

    setConfirmingPath(null);
    setSubmittingPath(group.targetPath);
    setResolveFailure(null);
    try {
      await resolveGroup(resolutions);
      await Promise.all(
        invalidateQueryKeys.map((key) =>
          queryClient.invalidateQueries({ queryKey: key })
        )
      );
      await groupsQuery.refetch();
      toast.success("Conflict group resolved");
    } catch (error: unknown) {
      setResolveFailure({
        title: "Nothing was resolved",
        messages: failureMessages(error, "Failed to resolve conflict group"),
      });
    } finally {
      setSubmittingPath(null);
    }
  };

  // ---- Filters + bulk resolution ----
  const availableExtensions = useMemo(() => {
    const set = new Set<string>();
    for (const g of groups)
      for (const f of g.files) {
        const e = extensionOf(f.filePath);
        if (e) set.add(e);
      }
    return Array.from(set).sort();
  }, [groups]);

  const availableReasons = useMemo(() => {
    const set = new Set<ConflictReason>();
    for (const g of groups)
      for (const f of g.files) if (f.conflictReason) set.add(f.conflictReason);
    return Array.from(set);
  }, [groups]);

  /** Count of files per conflict reason across the loaded groups — the breakdown shown in the header. */
  const reasonCounts = useMemo(() => {
    const counts: Partial<Record<ConflictReason, number>> = {};
    for (const g of groups)
      for (const f of g.files)
        if (f.conflictReason) counts[f.conflictReason] = (counts[f.conflictReason] ?? 0) + 1;
    return counts;
  }, [groups]);

  const filteredGroups = useMemo(
    () =>
      groups.filter((g) =>
        g.files.some(
          (f) =>
            (reasonFilter === "all" || f.conflictReason === reasonFilter) &&
            (extensionFilter === "all" || extensionOf(f.filePath) === extensionFilter)
        )
      ),
    [groups, reasonFilter, extensionFilter]
  );

  // What "Resolve all matching" acts on: in each visible group, the open conflicts that match the filters. A file
  // the filters leave out is never resolved just because a sibling in its group matched, and a settled or decided
  // conflict is not re-sent, with one exception: when one of the open conflicts is the new winner, the decided winner
  // it displaced goes with it, as with "Resolve group", or the server counts two winners and refuses the group.
  const bulkGroups = useMemo(
    () =>
      filteredGroups
        .map((g) => {
          const files = g.files.filter(
            (f) =>
              isOpenConflict(f) &&
              (reasonFilter === "all" || f.conflictReason === reasonFilter) &&
              (extensionFilter === "all" || extensionOf(f.filePath) === extensionFilter)
          );
          const displaced = files.some((f) => isWinner(f, actions))
            ? g.files.filter(
                (f) => !isOpenConflict(f) && touched.has(f.fileId) && isDisplacedWinner(f, actions)
              )
            : [];
          return { ...g, files: [...files, ...displaced] };
        })
        .filter((g) => g.files.length > 0),
    [filteredGroups, reasonFilter, extensionFilter, actions, touched]
  );

  // Eligible = a bulk group whose current actions form a valid resolution (no missing/extra winner).
  const eligibleGroups = bulkGroups.filter((g) => !groupState(g).invalid);
  const eligibleFileCount = eligibleGroups.reduce((n, g) => n + g.files.length, 0);
  const needsAttentionCount = bulkGroups.length - eligibleGroups.length;

  // Per action, how many files the bulk resolution will send — the confirmation states what each will do.
  const bulkActionCounts = useMemo(() => {
    const counts = new Map<ConflictResolutionAction, number>();
    for (const g of eligibleGroups)
      for (const f of g.files) {
        const action = actionFor(f);
        counts.set(action, (counts.get(action) ?? 0) + 1);
      }
    return counts;
  }, [eligibleGroups, actions]);
  const bulkOverwrites = bulkActionCounts.get(ConflictResolutionAction.ReplaceFromDisk) ?? 0;

  const isCollapsed = (group: ConflictGroupDto) =>
    collapseOverride[group.targetPath] ?? isGroupResolved(group);
  const toggleCollapse = (targetPath: string, value: boolean) =>
    setCollapseOverride((prev) => ({ ...prev, [targetPath]: value }));

  // Virtualize the group list so a firm's thousands-of-Office-files backlog only mounts the visible rows.
  const rowVirtualizer = useVirtualizer({
    count: filteredGroups.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => 132,
    overscan: 6,
    getItemKey: (index) => filteredGroups[index].targetPath,
  });

  /** Apply one action to every file carrying the given conflict reason (across all groups). */
  const setAllForReason = (
    reason: ConflictReason,
    action: ConflictResolutionAction
  ) => {
    // Open conflicts only: a bulk choice must not become a new decision on files already settled or decided.
    setActions((prev) => {
      const next = { ...prev };
      for (const g of groups)
        for (const f of g.files)
          if (f.conflictReason === reason && isOpenConflict(f)) next[f.fileId] = action;
      return next;
    });
  };

  const handleResolveAll = async () => {
    // A winner group submits alone, so a refusal costs only its own files; the rest batch together.
    const batches = buildResolveBatches(eligibleGroups, actions);
    const total = batches.reduce((n, b) => n + b.length, 0);
    setBulkConfirming(false);
    if (total === 0) return;

    setBulkProgress({ done: 0, total });
    setResolveFailure(null);
    let done = 0;
    try {
      for (const batch of batches) {
        await resolveGroup(batch);
        done += batch.length;
        setBulkProgress({ done, total });
      }
      toast.success(`Resolved ${total} file${total === 1 ? "" : "s"}`);
    } catch (error: unknown) {
      // A batch failed partway; surface it and still refresh so the UI shows what did commit.
      setResolveFailure({
        title: done > 0 ? `Stopped after ${done} of ${total} files` : "Nothing was resolved",
        messages: failureMessages(error, "Some conflicts could not be resolved"),
      });
    } finally {
      await Promise.all(
        invalidateQueryKeys.map((key) => queryClient.invalidateQueries({ queryKey: key }))
      );
      await groupsQuery.refetch();
      setBulkProgress(null);
    }
  };

  const bulkBusy = bulkProgress !== null;

  return (
    <TooltipProvider delayDuration={200}>
      <Button
        variant="outline"
        size="sm"
        className="min-w-44 gap-2 text-amber-600 dark:text-amber-400"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <GitMerge className="size-4" />
        Resolve conflicts
        {totalConflicts > 0 && (
          <Badge variant="warning" className="ml-1 px-1.5">
            {totalConflicts}
          </Badge>
        )}
      </Button>

      <Sheet
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          if (!value) setResolveFailure(null);
        }}
      >
        <SheetContent
          side="right"
          className="sm:max-w-2xl w-full gap-0"
        >
          <SheetHeader className="border-b">
            <SheetTitle className="flex items-center gap-2">
              <GitMerge className="size-5 text-amber-600 dark:text-amber-400" />
              Resolve conflicts
              <span className="ml-auto flex items-center gap-1">
                <DocsHelpLink to="how-to/resolve-conflicts" label="How to resolve conflicts" />
                <ConflictLegend />
              </span>
            </SheetTitle>
            <SheetDescription>
              Each group is one target path claimed by multiple files. Nominate at
              most one winner to replace from disk; every other file keeps the
              existing SharePoint copy unless you rename or skip it.
            </SheetDescription>
            <div className="mt-2 flex items-center justify-between gap-2">
              <Label htmlFor="show-resolved" className="text-xs text-muted-foreground">
                Show already-resolved conflicts (to change a decision)
              </Label>
              <Switch
                id="show-resolved"
                checked={showResolved}
                onCheckedChange={setShowResolved}
                disabled={disabled}
              />
            </div>

            {groups.length > 0 && (
              <div className="mt-2 flex flex-col gap-2 rounded-md border bg-muted/40 p-2">
                {availableReasons.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Breakdown:</span>
                    {availableReasons.map((r) => {
                      const ReasonIcon = ConflictReasonIcons[r];
                      return (
                        <Badge key={r} variant={ConflictReasonColors[r]} className="gap-1">
                          {ReasonIcon && <ReasonIcon className="size-3" />}
                          {ConflictReasonLabels[r]}
                          <span className="font-semibold">{reasonCounts[r] ?? 0}</span>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <ListFilter className="size-4 shrink-0 text-muted-foreground" />
                  <Select
                    value={reasonFilter}
                    onValueChange={(v) => setReasonFilter(v as ConflictReason | "all")}
                    disabled={disabled || bulkBusy}
                  >
                    <SelectTrigger size="sm" className="w-40">
                      <SelectValue placeholder="Reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All reasons</SelectItem>
                      {availableReasons.map((r) => (
                        <SelectItem key={r} value={r}>
                          {ConflictReasonLabels[r]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={extensionFilter}
                    onValueChange={(v) => setExtensionFilter(v)}
                    disabled={disabled || bulkBusy || availableExtensions.length === 0}
                  >
                    <SelectTrigger size="sm" className="w-36">
                      <SelectValue placeholder="File type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {availableExtensions.map((e) => (
                        <SelectItem key={e} value={e}>
                          {e}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {filteredGroups.length} of {groups.length} groups
                  </span>
                </div>

                <div className="flex flex-col gap-2">
                  {availableReasons.length > 0 && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <span className="text-xs text-muted-foreground">Set by reason:</span>
                      {availableReasons.map((r) => {
                        const ReasonIcon = ConflictReasonIcons[r];
                        return (
                          <div key={r} className="flex items-center gap-1.5">
                            <Badge variant={ConflictReasonColors[r]} className="gap-1">
                              {ReasonIcon && <ReasonIcon className="size-3" />}
                              {ConflictReasonLabels[r]}
                            </Badge>
                            <Select
                              onValueChange={(v) =>
                                setAllForReason(r, v as ConflictResolutionAction)
                              }
                              disabled={disabled || bulkBusy}
                            >
                              <SelectTrigger size="sm" className="w-44">
                                <SelectValue placeholder="Choose action…" />
                              </SelectTrigger>
                              <SelectContent>
                                {BULK_ACTIONS.map((a) => (
                                  <ActionSelectItem
                                    key={a}
                                    value={a}
                                    label={ConflictResolutionActionLabels[a]}
                                    description={ConflictResolutionActionDescriptions[a]}
                                    labelClassName={cn(
                                      colorVariantClasses[ConflictResolutionActionColors[a]]?.text
                                    )}
                                  />
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    {bulkBusy ? (
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="size-3.5 animate-spin" />
                        Resolving {bulkProgress!.done}/{bulkProgress!.total}…
                      </span>
                    ) : bulkConfirming ? (
                      <div className="flex flex-col gap-2">
                        <span className="text-xs text-muted-foreground">
                          Resolve {eligibleFileCount} conflict
                          {eligibleFileCount === 1 ? "" : "s"}?
                        </span>
                        <ul className="list-disc pl-5 text-xs text-muted-foreground">
                          {Array.from(bulkActionCounts.entries()).map(([action, count]) => (
                            <li key={action}>
                              {count} {ConflictResolutionActionLabels[action]}: {count === 1 ? "the file" : "the files"}{" "}
                              {ConflictResolutionOutcomeShortLabels[action]}
                            </li>
                          ))}
                        </ul>
                        {bulkOverwrites > 0 && (
                          <p className="flex items-center gap-1 text-xs text-destructive">
                            <AlertTriangle className="size-3.5 shrink-0" />
                            {bulkOverwrites} SharePoint cop{bulkOverwrites === 1 ? "y" : "ies"} will be overwritten.
                            An edit made to {bulkOverwrites === 1 ? "it" : "them"} in SharePoint is lost.
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => setBulkConfirming(false)}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            variant={bulkOverwrites > 0 ? "destructive" : "default"}
                            onClick={handleResolveAll}
                          >
                            {bulkOverwrites > 0 ? "Confirm overwrite" : "Confirm"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        className="gap-2"
                        disabled={disabled || eligibleFileCount === 0}
                        onClick={() => setBulkConfirming(true)}
                      >
                        <CheckCheck className="size-4" />
                        Resolve all matching ({eligibleFileCount})
                      </Button>
                    )}
                  </div>
                </div>

                {needsAttentionCount > 0 && (
                  <div className="flex flex-col gap-1 text-xs">
                    <p className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="size-3.5 shrink-0" />
                      {needsAttentionCount} group{needsAttentionCount === 1 ? "" : "s"} need a
                      winner (path collision) — resolve individually below.
                    </p>
                    {organizationId && (
                      <Link
                        to={conflictRulePagePath(organizationId)}
                        className="flex w-fit items-center gap-1 pl-[1.125rem] font-medium text-primary underline-offset-4 hover:underline"
                      >
                        <ListChecks className="size-3.5 shrink-0" />
                        Pick the winners of many groups at once with Resolve Conflicts by Rule
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            {resolveFailure && (
              <Alert variant="destructive" className="mt-2">
                <AlertTriangle />
                <AlertTitle>{resolveFailure.title}</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc space-y-1 pl-4 text-xs">
                    {resolveFailure.messages.map((message) => (
                      <li key={message} className="break-all">
                        {message}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => setResolveFailure(null)}
                  >
                    Dismiss
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </SheetHeader>

          <div ref={setScrollEl} className="flex-1 min-h-0 overflow-y-auto p-4">
            {groupsQuery.isLoading && (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Loading conflict groups…
              </div>
            )}

            {groupsQuery.isError && (
              <div className="flex flex-col items-center justify-center gap-3 py-12">
                <p className="text-sm text-muted-foreground">
                  Failed to load conflict groups.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => groupsQuery.refetch()}
                >
                  Retry
                </Button>
              </div>
            )}

            {!groupsQuery.isLoading &&
              !groupsQuery.isError &&
              filteredGroups.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                  <FolderTree className="size-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {groups.length === 0
                      ? showResolved
                        ? `No conflicts for this ${entityNoun}.`
                        : `No conflicts to resolve for this ${entityNoun}.`
                      : "No conflicts match the current filters."}
                  </p>
                </div>
              )}

            {!groupsQuery.isLoading &&
              !groupsQuery.isError &&
              filteredGroups.length > 0 && (
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    position: "relative",
                    width: "100%",
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const group = filteredGroups[virtualRow.index];
                    return (
                      <div
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <div className="pb-4">
                          <ConflictGroupCard
                            group={group}
                            state={groupState(group)}
                            actionFor={actionFor}
                            onFileAction={(file, action) =>
                              setFileAction(group, file, action)
                            }
                            collapsed={isCollapsed(group)}
                            onToggleCollapse={(value) =>
                              toggleCollapse(group.targetPath, value)
                            }
                            submitting={submittingPath === group.targetPath}
                            confirming={confirmingPath === group.targetPath}
                            onRequestResolve={() => requestResolveGroup(group)}
                            onConfirmResolve={() => handleResolveGroup(group)}
                            onCancelConfirm={() => setConfirmingPath(null)}
                            disabled={disabled}
                            bulkBusy={bulkBusy}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>

        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
};
